import { useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";

// A different shape than GiftViewerDialog.tsx's fire-ring (glow/flicker/
// Flame) on purpose: that pattern decorates a STATIC photo frame with a
// fixed ring of flames. A Together Mode reaction is a DISCRETE event, one
// per tap - closer to Instagram Live's floating hearts, a single emoji
// that rises, fades, and is gone. Each burst removes its own appState
// entry via onDone once its animation finishes (see TogetherRoot.tsx).
const rise = keyframes`
  0% { transform: translateY(0) scale(0.6); opacity: 0; }
  15% { opacity: 1; transform: translateY(-16px) scale(1.15); }
  80% { opacity: 1; }
  100% { transform: translateY(-160px) scale(1); opacity: 0; }
`;

const BurstEmoji = styled.span<{ $left: number }>`
  position: fixed;
  bottom: calc(72px + env(safe-area-inset-bottom, 0px));
  left: ${(p) => p.$left}%;
  font-size: 2rem;
  line-height: 1;
  animation: ${rise} 1.6s ease-out forwards;
  pointer-events: none;
  z-index: 1400;
  filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.35));
`;

const BURST_DURATION_MS = 1600;

export function ReactionBurst({ emoji, onDone }: { emoji: string; onDone: () => void }) {
  // Stable per-instance horizontal jitter so a run of reactions doesn't
  // stack in a single column - randomized once, not on every re-render.
  // Roughly over the reaction toolbar (now bottom-left, not centered -
  // see TogetherRoot.tsx), so bursts visually originate from where they
  // were actually tapped rather than the middle of the screen.
  const leftRef = useRef(10 + Math.random() * 24);

  useEffect(() => {
    const timer = setTimeout(onDone, BURST_DURATION_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <BurstEmoji $left={leftRef.current}>{emoji}</BurstEmoji>;
}

// The BUILDING half of a press-and-hold reaction (TogetherRoot.tsx) - a
// persistent, glowing badge that scales/brightens smoothly as `intensity`
// climbs (1-4), driven entirely by CSS transitions on a single mounted
// element rather than restarting an animation per level. Deliberately NOT
// the rise-and-fade ReactionBurst animation - that's reserved for the
// RELEASE moment: when a hold ends, the caller pushes one final
// emoji.repeat(intensity) entry through the exact same one-shot
// ReactionBurst pathway taps already use, so "letting go" gets the
// familiar float-up-and-fade for free instead of a second bespoke
// animation.
const glow = keyframes`
  0%, 100% { filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.35)) drop-shadow(0 0 2px rgba(255, 90, 154, 0.55)); }
  50% { filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.35)) drop-shadow(0 0 10px rgba(255, 90, 154, 0.85)); }
`;

// $variant separates "my own hold" (bottom-left, near the reaction tray
// this gesture actually came from) from "my partner's hold" (bottom-right)
// so both can render independently and simultaneously without overlapping
// (spec's own "both hold reactions simultaneously" case).
const HoldBadge = styled.div<{ $intensity: number; $variant: "own" | "peer" }>`
  position: fixed;
  ${(p) => (p.$variant === "own" ? "left: 24px;" : "right: 24px;")}
  bottom: calc(88px + env(safe-area-inset-bottom, 0px));
  z-index: 1400;
  line-height: 1;
  font-size: ${(p) => 1.3 + p.$intensity * 0.35}rem;
  transition:
    font-size 0.25s ease,
    transform 0.25s ease;
  transform: scale(${(p) => 1 + p.$intensity * 0.06});
  animation: ${glow} 1.1s ease-in-out infinite;
  pointer-events: none;
`;

export function HoldReactionBadge({ emoji, intensity, variant }: { emoji: string; intensity: number; variant: "own" | "peer" }) {
  return (
    <HoldBadge $intensity={intensity} $variant={variant} aria-hidden="true">
      {emoji.repeat(intensity)}
    </HoldBadge>
  );
}
